import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Briefcase,
  Pencil,
  Users,
  DollarSign,
  Calendar,
  UserCheck,
  Building2,
  FileText,
  CheckCircle,
  XCircle,
} from 'lucide-react'
import { useClientesDelServicio } from '@/hooks/useServicios'
import { usePermissions } from '@/hooks/usePermissions'
import type { ServicioConClientes } from '@/services/serviciosService'
import { formatDateOrDash as formatDate } from '@/lib/utils'
import {
  CATEGORIA_SERVICIO_LABELS,
  PERIODICIDAD_SERVICIO_LABELS,
  type CategoriaServicio,
  type PeriodicidadServicio,
  type EstadoServicio,
} from '@/types/database'

interface ServicioDetalleDialogProps {
  servicio: ServicioConClientes | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onEditar: (servicio: ServicioConClientes) => void
}

// Tipo extendido
type ServicioExtendido = ServicioConClientes & {
  descripcion?: string | null
  categoria?: CategoriaServicio
  precio_estimado?: number | null
  periodicidad?: PeriodicidadServicio | null
  estado?: EstadoServicio
  aplica_persona_fisica?: boolean
  aplica_persona_juridica?: boolean
  aplica_sucesion_indivisa?: boolean
  created_at?: string | null
}

// Formato de moneda
const formatCurrency = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
  }).format(value)
}

// Badge de categoria
function getCategoriaBadge(categoria: CategoriaServicio | undefined) {
  if (!categoria) return null
  return (
    <Badge variant="secondary">
      {CATEGORIA_SERVICIO_LABELS[categoria]}
    </Badge>
  )
}

export function ServicioDetalleDialog({
  servicio,
  open,
  onOpenChange,
  onEditar,
}: ServicioDetalleDialogProps) {
  const ext = servicio as ServicioExtendido | null
  const { puede } = usePermissions()

  // Query para obtener los clientes del servicio
  const { data: clientes = [], isLoading: loadingClientes } = useClientesDelServicio(
    servicio?.id || ''
  )

  // Calcular total de honorarios asignados (suma de precios de clientes)
  const totalHonorariosAsignados = clientes.reduce((total, c) => {
    return total + (c.precio || 0)
  }, 0)

  if (!servicio || !ext) return null

  const isActivo = ext.estado === 'activo'

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col p-0">
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Briefcase className="h-5 w-5 text-primary" />
              </div>
              <div>
                <SheetTitle className="text-left">{servicio.nombre}</SheetTitle>
                <SheetDescription className="text-left">
                  {ext.categoria && CATEGORIA_SERVICIO_LABELS[ext.categoria]}
                </SheetDescription>
              </div>
            </div>
            <Badge variant={isActivo ? 'default' : 'secondary'}>
              {isActivo ? 'Activo' : 'Inactivo'}
            </Badge>
          </div>
        </SheetHeader>

        {/* Contenido scrolleable */}
        <ScrollArea className="flex-1">
          <div className="px-6 py-4 space-y-6">
            {/* Estadisticas rapidas */}
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg border bg-card p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Users className="h-4 w-4" />
                  <span className="text-xs">Clientes</span>
                </div>
                <p className="text-2xl font-bold">{servicio.clientesCount}</p>
              </div>
              {puede.verHonorariosCliente && (
                <div className="rounded-lg border bg-card p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <DollarSign className="h-4 w-4" />
                    <span className="text-xs">Total Honorarios</span>
                  </div>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(totalHonorariosAsignados)}
                  </p>
                </div>
              )}
            </div>

            <Separator />

            {/* Informacion general */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Informacion General
              </h3>

              <div className="grid gap-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Categoria</span>
                  {getCategoriaBadge(ext.categoria)}
                </div>
                {puede.verHonorariosCliente && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Precio de Referencia</span>
                    <span className="font-mono">{formatCurrency(ext.precio_estimado)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Periodicidad</span>
                  <span>
                    {ext.periodicidad ? PERIODICIDAD_SERVICIO_LABELS[ext.periodicidad] : '-'}
                  </span>
                </div>
                {ext.descripcion && (
                  <div className="space-y-1">
                    <span className="text-muted-foreground">Descripcion</span>
                    <p className="text-sm bg-muted/50 rounded p-2">{ext.descripcion}</p>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Aplica a */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Aplica a Tipo de Persona
              </h3>

              <div className="flex flex-wrap gap-2">
                <div className="flex items-center gap-2 text-sm">
                  {ext.aplica_persona_fisica ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-muted-foreground" />
                  )}
                  <UserCheck className="h-4 w-4" />
                  <span className={!ext.aplica_persona_fisica ? 'text-muted-foreground' : ''}>
                    Persona Fisica
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  {ext.aplica_persona_juridica ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-muted-foreground" />
                  )}
                  <Building2 className="h-4 w-4" />
                  <span className={!ext.aplica_persona_juridica ? 'text-muted-foreground' : ''}>
                    Sociedad
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  {ext.aplica_sucesion_indivisa ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-muted-foreground" />
                  )}
                  <FileText className="h-4 w-4" />
                  <span className={!ext.aplica_sucesion_indivisa ? 'text-muted-foreground' : ''}>
                    Sucesion Indivisa
                  </span>
                </div>
              </div>
            </div>

            <Separator />

            {/* Clientes asignados: cada fila trae el precio del cliente */}
            {puede.verHonorariosCliente && (
            <div className="space-y-4">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Users className="h-4 w-4" />
                Clientes Asignados ({servicio.clientesCount})
              </h3>

              {loadingClientes ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-12 bg-muted animate-pulse rounded" />
                  ))}
                </div>
              ) : clientes.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No hay clientes asignados a este servicio
                </p>
              ) : (
                <div className="space-y-2">
                  {clientes.map((cliente) => (
                    <div
                      key={cliente.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary">
                            {cliente.nombre.charAt(0)}
                          </span>
                        </div>
                        <span className="text-sm font-medium">{cliente.nombre}</span>
                      </div>
                      <span className="text-sm font-mono text-muted-foreground">
                        {formatCurrency(cliente.precio)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}

            {/* Metadata */}
            <div className="text-xs text-muted-foreground pt-4">
              Creado: {formatDate(ext.created_at)}
            </div>
          </div>
        </ScrollArea>

        {/* Footer con acciones */}
        <div className="px-6 py-4 border-t bg-background">
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cerrar
            </Button>
            <Button onClick={() => onEditar(servicio)}>
              <Pencil className="h-4 w-4 mr-2" />
              Editar
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
